import type { ReactElement } from 'react'
import { CollapseIndicator } from '../CollapseIndicator'
import {
  CONNECTION_POINT_RADIUS,
  CONNECTION_STROKE_WIDTH,
  DROP_LINE_WIDTH,
  HOVER_HIT_RADIUS,
  PARENT_FILL_COLOR,
  TREE_LINE_COLOR,
  TREE_LINE_WIDTH,
  type BranchLine,
  type TreeNode,
} from './tree-topology.helpers'

interface BranchLineSegmentProps {
  branch: BranchLine
}

export function BranchLineSegment({ branch }: BranchLineSegmentProps): ReactElement {
  return (
    <>
      <line
        x1={branch.minX}
        y1={branch.branchY}
        x2={branch.maxX}
        y2={branch.branchY}
        stroke={TREE_LINE_COLOR}
        strokeWidth={TREE_LINE_WIDTH}
        strokeLinecap="round"
        className="dark:stroke-slate-400"
      />
      <line
        x1={branch.parentPosition.x}
        y1={branch.parentPosition.y}
        x2={branch.parentPosition.x}
        y2={branch.branchY}
        stroke={TREE_LINE_COLOR}
        strokeWidth={DROP_LINE_WIDTH}
        className="dark:stroke-slate-400"
      />
    </>
  )
}

interface ParentConnectionProps {
  branch: BranchLine
  hovered: boolean
  onHover: (parentId: string | null) => void
  onToggleCollapse?: ((endpointId: string) => void) | undefined
  onToggleCollapseRecursive?: ((endpointId: string) => void) | undefined
}

export function ParentConnection({
  branch,
  hovered,
  onHover,
  onToggleCollapse,
  onToggleCollapseRecursive,
}: ParentConnectionProps): ReactElement {
  return (
    <g
      style={{ pointerEvents: 'auto' }}
      onMouseEnter={() => {
        onHover(branch.parentId)
      }}
      onMouseLeave={() => {
        onHover(null)
      }}
    >
      <circle
        cx={branch.parentPosition.x}
        cy={branch.branchY}
        r={HOVER_HIT_RADIUS}
        fill="transparent"
        style={{ cursor: 'pointer' }}
      />
      <circle
        cx={branch.parentPosition.x}
        cy={branch.branchY}
        r={CONNECTION_POINT_RADIUS}
        fill={PARENT_FILL_COLOR}
        stroke="white"
        strokeWidth={CONNECTION_STROKE_WIDTH}
      />
      {branch.childCount > 1 && onToggleCollapse && onToggleCollapseRecursive && (
        <CollapseIndicator
          position={{ x: branch.parentPosition.x, y: branch.branchY }}
          isCollapsed={branch.isCollapsed}
          childCount={branch.childCount}
          isVisible={hovered}
          onClick={() => {
            onToggleCollapse(branch.parentId)
          }}
          onShiftClick={() => {
            onToggleCollapseRecursive(branch.parentId)
          }}
        />
      )}
    </g>
  )
}

interface ChildConnectionProps {
  child: TreeNode
  branchY: number
}

export function ChildConnection({ child, branchY }: ChildConnectionProps): ReactElement {
  return (
    <g key={`child-conn-${child.endpointId}`}>
      <line
        x1={child.position.x}
        y1={branchY}
        x2={child.position.x}
        y2={child.position.y}
        stroke={TREE_LINE_COLOR}
        strokeWidth={DROP_LINE_WIDTH}
        className="dark:stroke-slate-400"
      />
      <circle
        cx={child.position.x}
        cy={branchY}
        r={CONNECTION_POINT_RADIUS}
        fill={child.hasChildren ? PARENT_FILL_COLOR : TREE_LINE_COLOR}
        stroke="white"
        strokeWidth={CONNECTION_STROKE_WIDTH}
        className={child.hasChildren ? '' : 'dark:fill-slate-400'}
      />
    </g>
  )
}

interface BranchProps {
  branch: BranchLine
  hovered: boolean
  onHover: (parentId: string | null) => void
  onToggleCollapse?: ((endpointId: string) => void) | undefined
  onToggleCollapseRecursive?: ((endpointId: string) => void) | undefined
}

export function Branch({
  branch,
  hovered,
  onHover,
  onToggleCollapse,
  onToggleCollapseRecursive,
}: BranchProps): ReactElement {
  return (
    <g key={`branch-${branch.parentId}`}>
      <BranchLineSegment branch={branch} />
      <ParentConnection
        branch={branch}
        hovered={hovered}
        onHover={onHover}
        onToggleCollapse={onToggleCollapse}
        onToggleCollapseRecursive={onToggleCollapseRecursive}
      />
      {branch.children.map((child) => (
        <ChildConnection key={child.endpointId} child={child} branchY={branch.branchY} />
      ))}
    </g>
  )
}
